import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isCancel } from 'axios';

import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Icons,
  Text,
  useTheme,
} from '@ublend-npm/aulaui-core';

import { usersSearch } from '@core/api/search';
import { SelectedUser } from '@core/conversations/types';
import { currentUserId } from '@core/selectors/user';
import { AulaState } from '@core/types/state';
import { getCurrentSpaceId } from '@core/selectors/space';
import useBreakpoints from '@app/hooks/useBreakpoints';
import pushRoute from '@app/utils/routing';
import ConversationParticipantsListResponsiveContainer from './MessageListAndTools/ConversationModal/modals/ConversationParticipantsListResponsiveContainer';

type InviteParticipantsProps = {
  onClose?: () => void;
  conversationId?: string;
};

const TIMEOUT_MS = 500;

const InviteParticipants = ({
  onClose,
  conversationId,
}: InviteParticipantsProps) => {
  const [searchText, setSearchText] = useState('');
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [options, setOptions] = useState<SelectedUser[] | null>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<
    SelectedUser[]
  >([]);
  const [showViewParticipants, setShowViewParticipants] = useState(false);
  const [waitingForUpdate, setWaitingForUpdate] = useState(false);
  const [expectedParticipantCount, setExpectedParticipantCount] = useState(0);

  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const { palette } = useTheme();
  const { lg } = useBreakpoints();
  const spaceId = useSelector(getCurrentSpaceId);
  const dispatch = useDispatch();

  const userId = useSelector(currentUserId);

  // Get existing conversation participants to filter them out
  const conversation = useSelector((state: AulaState) => {
    if (!conversationId) return null;
    return state.data?.inboxv2?.conversations?.[conversationId];
  });

  const existingParticipantIds = conversation?.participants
    ? Object.keys(conversation.participants)
    : [];

  const handleUserSearch = (value: string) => {
    if (timeoutId.current) clearTimeout(timeoutId.current);
    setOptions(null);
    timeoutId.current = setTimeout(async () => {
      setLoadingSearch(true);
      try {
        const results = await usersSearch({
          query: value,
          spaceId: null,
          includeEducatorInSpaces: false,
        });
        setOptions(results);
      } catch (error) {
        if (!isCancel(error)) {
          console.error(error);
        }
      }
      setLoadingSearch(false);
      timeoutId.current = null;
    }, TIMEOUT_MS);
  };

  const handleInviteParticipants = async () => {
    if (!conversationId || selectedParticipants.length === 0) return;

    setLoadingInvite(true);
    try {
      const { postParticipant } = await import(
        '../../../../core/conversations/api/conversations'
      );

      for (const participant of selectedParticipants) {
        await postParticipant(
          conversationId,
          participant.objectId || participant.id
        );
      }

      if (lg) {
        const currentCount = Object.keys(
          conversation?.participants || {}
        ).length;
        setExpectedParticipantCount(currentCount + selectedParticipants.length);
        setWaitingForUpdate(true);
      } else {
        pushRoute(`dashboard/${spaceId}/messages/${conversationId}`);

        setTimeout(async () => {
          const { default: openViewParticipants } = await import(
            '../../../../core/conversations/operationsResponsive/openViewParticipants.action'
          );
          dispatch(openViewParticipants());
        }, 500);
      }

      setSelectedParticipants([]);
    } catch (error) {
      console.error('Error inviting participants:', error);
    }
    setLoadingInvite(false);
  };

  // Effect to watch for conversation updates and show modal when ready (large screens)
  useEffect(() => {
    if (lg && waitingForUpdate && conversation) {
      const currentCount = Object.keys(conversation.participants || {}).length;
      if (currentCount >= expectedParticipantCount) {
        setWaitingForUpdate(false);
        setShowViewParticipants(true);
      }
    }
  }, [lg, conversation, waitingForUpdate, expectedParticipantCount]);

  const handleViewParticipantsClose = () => {
    setShowViewParticipants(false);
    onClose?.();
  };

  return (
    <>
      {lg && showViewParticipants && conversation && (
        <ConversationParticipantsListResponsiveContainer
          handleClose={handleViewParticipantsClose}
          groupId={conversationId!}
          boundToSpaceGroup={!!conversation.boundToSpaceGroup}
          openn={showViewParticipants}
          classRoomId={conversation.space || ''}
          userId={userId || ''}
          educatorOnly={!!conversation.educatorOnly}
          conversationData={conversation}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Text kind="h4">Invite Participants</Text>
        {onClose && (
          <IconButton aria-label="Close Popup" onClick={onClose}>
            <Icons.Close />
          </IconButton>
        )}
      </Box>
      <Box sx={{ marginTop: 2 }}>
        <Autocomplete
          sx={{ maxWidth: 'calc(100dvw - 32px)' }}
          multiple
          label="Enter name of participants"
          loading={loadingSearch}
          options={options || []}
          getOptionLabel={(o: SelectedUser) => `${o.firstName} ${o.lastName}`}
          filterOptions={(options) =>
            options.filter(
              (o) =>
                o.id !== userId &&
                !existingParticipantIds.includes(o.id) &&
                !selectedParticipants.map((c) => c.id).includes(o.id)
            )
          }
          noOptionsText={
            searchText === '' || options === null
              ? 'Type to search'
              : 'No matches found'
          }
          inputValue={searchText}
          onInputChange={(_, value) => {
            setSearchText(value);
            handleUserSearch(value);
          }}
          value={selectedParticipants}
          onChange={(_, newVals) => setSelectedParticipants(newVals || [])}
        />
      </Box>
      <Box
        sx={{
          marginTop: 2,
          display: 'flex',
          gap: 2,
          justifyContent: 'end',
        }}
      >
        <Button
          sx={{
            textTransform: 'none',
            '&:hover': {
              borderColor: palette.primary.dark,
            },
          }}
          kind="secondary"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          sx={{
            textTransform: 'none',
            '&:hover': {
              backgroundColor: palette.primary.dark,
            },
          }}
          onClick={handleInviteParticipants}
          loading={loadingInvite}
          disabled={selectedParticipants.length < 1}
        >
          Invite
        </Button>
      </Box>
    </>
  );
};

export default InviteParticipants;
